import { graphql } from "gatsby";
import PropTypes from "prop-types";
import React, { ReactElement } from "react";
import Helmet from "react-helmet";
import clsx from "clsx";

import { Container, Typography } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";

import Content, { HTMLContent } from "../components/Content";
import Layout from "../components/Layout";
import PreviewCompatibleImage from "../components/PreviewCompatibleImage";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: "grid",
      gap: `${theme.spacing(2)}px`,
      gridArea: "content",
      paddingBottom: theme.spacing(4),
      paddingTop: theme.spacing(4),
      width: "100vw"
    },
    title: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    heading: {
      fontSize: "16pt",
      fontWeight: "bold"
    },
    subheading: {
      fontSize: "14pt",
      fontWeight: "bold"
    },
    image: {
      [theme.breakpoints.down("sm")]: {
        margin: "auto",
      },
      [theme.breakpoints.up("sm")]: {
        margin: "auto",
      }
    },
    description: {}
  })
);

export const AwardsPageTemplate = ({
  title,
  heading,
  subheading,
  description,
  featuredimage,
  imagewidth,
  content,
  contentComponent,
  helmet
}): ReactElement => {
  const classes = useStyles();
  const PageContent = contentComponent || Content;

  return (
    <Container className={classes.root} component="main" fixed>
      {helmet || ""}
      {/* <Typography component="h1" variant="h4" className={classes.title}>
        {title}
      </Typography> */}
      {heading && (
          <Typography
            className={clsx(classes.heading)}
            component="h1"
            variant="h2"
          >
            {heading}
          </Typography>
        )}
        {featuredimage ? (
          <PreviewCompatibleImage
            imageInfo={{
              image: featuredimage,
              alt: heading,
              style: {
                width: `${imagewidth}px`,
                minWidth: '370px',
                maxWidth: '1024px',
                margin: "1rem auto",
              }
            }}
          />
        ) : null}
        {subheading && (
          <Typography
            className={clsx(classes.subheading)}
            component="h2"
            variant="h3"
          >
            {subheading}
          </Typography>
        )}
        {description && (
          <Typography className={clsx(classes.description)} component="p">
            {description}
          </Typography>
        )}
      <PageContent className="content" content={content} />
    </Container>
  );
};

AwardsPageTemplate.propTypes = {
  title: PropTypes.string.isRequired,
  heading: PropTypes.string,
  featuredimage: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  imagewidth: PropTypes.string,
  subheading: PropTypes.string,
  description: PropTypes.string,
  content: PropTypes.string,
  contentComponent: PropTypes.func,
  helmet: PropTypes.object
};

const AwardsPage = ({ data, location }): ReactElement => {
  const { markdownRemark: post } = data;
  const rootUrl = "https://williamslynch.co.uk";

  return (
    <Layout pathname={location.pathname}>
      <AwardsPageTemplate
        contentComponent={HTMLContent}
        title={post.frontmatter.title}
        heading={post.frontmatter.heading}
        subheading={post.frontmatter.subheading}
        description={post.frontmatter.description}
        featuredimage={post.frontmatter.featuredimage}
        imagewidth={post.frontmatter.imagewidth}
        content={post.html}
        helmet={
          <Helmet titleTemplate="%s">
            <title>{`${post.frontmatter.title}`}</title>
            <meta name="description" content={`${post.frontmatter.description}`} />
            <meta name="image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="og:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
            <meta name="twitter:image" content={`${rootUrl}${post.frontmatter.featuredimage?.childImageSharp.fluid.src}`} />
          </Helmet>
        }
      />
    </Layout>
  );
};

AwardsPage.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object
};

export default AwardsPage;

export const pageQuery = graphql`
  query AwardsPage($id: String!) {
    markdownRemark(id: { eq: $id }) {
      html
      frontmatter {
        title
        heading
        subheading
        featuredimage {
          childImageSharp {
            fluid(maxWidth: 960, quality: 64) {
              ...GatsbyImageSharpFluid
            }
          }
        }
        imagewidth
        description
      }
    }
  }
`;
